/**
 * Property Elevation Canvas Renderer
 * 
 * Converts PropertyElevationDiagram data to canvas for PDF export
 * Matches the visual style of the interactive SVG diagram
 */

interface PlacedElement {
  id: string;
  type: 'window' | 'door' | 'airvent';
  x: number;
  y: number;
  elevation: string;
  height?: number;
  width?: number;
}

interface ElevationData {
  type: string;
  dimensions: { height: number; width: number };
  placedElements: PlacedElement[];
  wallInsulationType?: string;
  buildingHeight: number;
}

interface PropertyData {
  propertyType: string;
  roofTypeRdSAP: string;
  groundLevel: string;
  detachment: string;
  floorCount: number;
}

/**
 * Generate elevation canvas for PDF export
 * Matches the PropertyElevationDiagram rendering exactly
 */
export async function generatePropertyElevationCanvas(
  elevationData: ElevationData,
  propertyData: PropertyData
): Promise<HTMLCanvasElement | null> {
  try {
    const { dimensions, placedElements, wallInsulationType, buildingHeight } = elevationData;
    
    // Calculate dimensions (matching PropertyElevationDiagram logic EXACTLY)
    const baseBuildingHeight = buildingHeight || 1;
    const showBasement = propertyData.groundLevel !== 'Ground level';
    const roofType = propertyData.roofTypeRdSAP || '';
    const showRoof = roofType && roofType !== 'Flat roof' && roofType !== 'flat';
    const roofStyle = roofType.toLowerCase().includes('flat') ? 'flat' : 'pitched';
    
    // Adaptive dimensions matching PropertyElevationDiagram's getAdaptiveDimensions() EXACTLY
    const isSingleStory = baseBuildingHeight === 1;
    let storyHeight: number;
    let buildingWidth: number;
    let buildingX: number;
    let groundY: number;
    let svgWidth: number;
    let svgHeight: number;
    
    if (isSingleStory) {
      // Single story configuration - EXACT match
      svgWidth = 500;
      svgHeight = 420;
      storyHeight = 204; // 120 * 1.7 = 204 (70% increase)
      buildingWidth = 350;
      buildingX = 75;
      groundY = 370; // NOT 320! This is the key difference
    } else {
      // Multi-story configuration - EXACT match
      const baseStoryHeight = baseBuildingHeight <= 2 ? 136 : Math.max(102, 238 / baseBuildingHeight); // 80 * 1.7 = 136, 60 * 1.7 = 102, 140 * 1.7 = 238
      const totalRequiredHeight = (baseBuildingHeight * baseStoryHeight) + (showRoof ? 80 : 0) + (showBasement ? 40 : 0) + 80; // 80 for margins
      const adaptiveHeight = Math.max(450, totalRequiredHeight);
      
      svgWidth = 500;
      svgHeight = adaptiveHeight;
      storyHeight = baseStoryHeight;
      buildingWidth = 350;
      buildingX = 75;
      groundY = adaptiveHeight - 80; // Keep some margin from bottom - DYNAMIC!
    }
    
    const basementHeight = showBasement ? 40 : 0;
    const buildingY = groundY - (baseBuildingHeight * storyHeight) - basementHeight;
    
    // Create canvas
    const canvas = document.createElement('canvas');
    const scale = 2; // High DPI for crisp rendering
    canvas.width = svgWidth * scale;
    canvas.height = svgHeight * scale;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      console.error('[ElevationCanvas] Failed to get 2D context');
      return null;
    }
    
    // Scale for high DPI
    ctx.scale(scale, scale);
    
    // Fill white background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, svgWidth, svgHeight);
    
    // Draw ground line
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, groundY);
    ctx.lineTo(svgWidth, groundY);
    ctx.stroke();
    
    // Draw basement if applicable
    if (showBasement) {
      ctx.fillStyle = 'white';
      ctx.strokeStyle = 'black';
      ctx.lineWidth = 1;
      ctx.fillRect(buildingX, groundY - basementHeight, buildingWidth, basementHeight);
      ctx.strokeRect(buildingX, groundY - basementHeight, buildingWidth, basementHeight);
    }
    
    // Draw main building structure
    ctx.fillStyle = 'white';
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 2;
    ctx.fillRect(buildingX, buildingY, buildingWidth, baseBuildingHeight * storyHeight);
    ctx.strokeRect(buildingX, buildingY, buildingWidth, baseBuildingHeight * storyHeight);
    
    // Draw DPC section (bottom band)
    const dpcHeight = 20;
    ctx.fillStyle = '#f5f5f5';
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 1;
    ctx.fillRect(buildingX, buildingY + (baseBuildingHeight * storyHeight) - dpcHeight, buildingWidth, dpcHeight);
    ctx.strokeRect(buildingX, buildingY + (baseBuildingHeight * storyHeight) - dpcHeight, buildingWidth, dpcHeight);
    
    // Draw cavity brickwork pattern if cavity insulation
    if (wallInsulationType === 'cavity') {
      renderCavityBrickwork(ctx, buildingX, buildingY, buildingWidth, (baseBuildingHeight * storyHeight) - dpcHeight);
    }
    
    // Draw floor division lines for multi-storey
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 3;
    for (let i = 0; i < baseBuildingHeight - 1; i++) {
      const y = buildingY + ((i + 1) * storyHeight);
      ctx.beginPath();
      ctx.moveTo(buildingX, y);
      ctx.lineTo(buildingX + buildingWidth, y);
      ctx.stroke();
    }
    
    // Draw roof
    if (showRoof) {
      if (roofStyle === 'pitched') {
        // Pitched roof triangle
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(buildingX, buildingY);
        ctx.lineTo(buildingX + buildingWidth / 2, buildingY - 60);
        ctx.lineTo(buildingX + buildingWidth, buildingY);
        ctx.stroke();
        
        // Ridge line
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(buildingX + buildingWidth / 2, buildingY - 60);
        ctx.lineTo(buildingX + buildingWidth / 2, buildingY);
        ctx.stroke();
      } else if (roofStyle === 'flat') {
        // Flat roof structure
        ctx.fillStyle = '#d3d3d3';
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 2;
        ctx.fillRect(buildingX, buildingY - 15, buildingWidth, 15);
        ctx.strokeRect(buildingX, buildingY - 15, buildingWidth, 15);
        
        // Parapet walls
        ctx.fillStyle = 'white';
        ctx.lineWidth = 1;
        ctx.fillRect(buildingX - 5, buildingY - 25, 5, 25);
        ctx.strokeRect(buildingX - 5, buildingY - 25, 5, 25);
        ctx.fillRect(buildingX + buildingWidth, buildingY - 25, 5, 25);
        ctx.strokeRect(buildingX + buildingWidth, buildingY - 25, 5, 25);
        
        // Drainage outlets
        ctx.fillStyle = 'black';
        ctx.beginPath();
        ctx.arc(buildingX + buildingWidth * 0.25, buildingY - 7, 3, 0, 2 * Math.PI);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(buildingX + buildingWidth * 0.75, buildingY - 7, 3, 0, 2 * Math.PI);
        ctx.fill();
      }
    }
    
    // Draw placed elements
    renderPlacedElements(ctx, placedElements, elevationData.type);
    
    return canvas;
  } catch (error) {
    console.error('[ElevationCanvas] Error generating canvas:', error);
    return null;
  }
}

/**
 * Render cavity brickwork pattern
 */
function renderCavityBrickwork(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number
) {
  const brickHeight = 8;
  const brickWidth = 24;
  const mortarThickness = 2;
  const rows = Math.floor(height / (brickHeight + mortarThickness));
  const cols = Math.floor(width / (brickWidth + mortarThickness));
  
  ctx.fillStyle = 'white';
  ctx.strokeStyle = '#8B4513';
  ctx.lineWidth = 0.5;
  
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const brickX = x + col * (brickWidth + mortarThickness) + (row % 2) * (brickWidth / 2);
      const brickY = y + row * (brickHeight + mortarThickness);
      
      if (brickX + brickWidth <= x + width && brickY + brickHeight <= y + height) {
        ctx.fillRect(brickX, brickY, brickWidth, brickHeight);
        ctx.strokeRect(brickX, brickY, brickWidth, brickHeight);
      }
    }
  }
}

/**
 * Render placed elements (windows, doors, vents)
 */
function renderPlacedElements(
  ctx: CanvasRenderingContext2D,
  placedElements: PlacedElement[],
  elevationType: string
) {
  const currentElements = placedElements.filter(el => el.elevation === elevationType);
  
  // Generate element IDs for labeling
  const elementCounts: Record<string, number> = { window: 0, door: 0, airvent: 0 };
  
  currentElements.forEach((element) => {
    elementCounts[element.type]++;
    const elementId = elementCounts[element.type];
    
    switch (element.type) {
      case 'window':
        renderWindow(ctx, element.x, element.y, elementId);
        break;
      case 'door':
        renderDoor(ctx, element.x, element.y, elementId);
        break;
      case 'airvent':
        renderAirVent(ctx, element.x, element.y, elementId);
        break;
    }
  });
}

/**
 * Render window element
 */
function renderWindow(ctx: CanvasRenderingContext2D, x: number, y: number, id: number) {
  // Window frame
  ctx.fillStyle = 'rgba(135, 206, 250, 0.8)';
  ctx.strokeStyle = '#1e40af';
  ctx.lineWidth = 2;
  
  roundRect(ctx, x - 20, y - 15, 40, 30, 2);
  ctx.fill();
  ctx.stroke();
  
  // Window cross
  ctx.strokeStyle = '#1e40af';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y - 15);
  ctx.lineTo(x, y + 15);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x - 20, y);
  ctx.lineTo(x + 20, y);
  ctx.stroke();
  
  // ID label
  ctx.fillStyle = '#1e40af';
  ctx.strokeStyle = 'white';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(x - 15, y - 10, 8, 0, 2 * Math.PI);
  ctx.fill();
  ctx.stroke();
  
  ctx.fillStyle = 'white';
  ctx.font = 'bold 10px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(id.toString(), x - 15, y - 10);
}

/**
 * Render door element
 */
function renderDoor(ctx: CanvasRenderingContext2D, x: number, y: number, id: number) {
  // Door panel
  ctx.fillStyle = '#d97706';
  ctx.strokeStyle = '#92400e';
  ctx.lineWidth = 2;
  
  roundRect(ctx, x - 20, y - 40, 40, 80, 2);
  ctx.fill();
  ctx.stroke();
  
  // Door panels
  ctx.strokeStyle = '#92400e';
  ctx.lineWidth = 1;
  roundRect(ctx, x - 16, y - 35, 32, 30, 1);
  ctx.stroke();
  roundRect(ctx, x - 16, y, 32, 30, 1);
  ctx.stroke();
  
  // Door handle
  ctx.fillStyle = '#92400e';
  ctx.beginPath();
  ctx.arc(x + 12, y - 5, 2, 0, 2 * Math.PI);
  ctx.fill();
  
  // ID label
  ctx.fillStyle = '#d97706';
  ctx.strokeStyle = 'white';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(x - 15, y - 30, 8, 0, 2 * Math.PI);
  ctx.fill();
  ctx.stroke();
  
  ctx.fillStyle = 'white';
  ctx.font = 'bold 10px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(id.toString(), x - 15, y - 30);
}

/**
 * Render air vent element
 */
function renderAirVent(ctx: CanvasRenderingContext2D, x: number, y: number, id: number) {
  // Vent body
  ctx.fillStyle = '#374151';
  ctx.strokeStyle = '#111827';
  ctx.lineWidth = 1;
  
  roundRect(ctx, x - 6, y - 3, 12, 6, 1);
  ctx.fill();
  ctx.stroke();
  
  // Grill lines
  ctx.strokeStyle = '#111827';
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(x - 4, y - 1);
  ctx.lineTo(x + 4, y - 1);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x - 4, y + 1);
  ctx.lineTo(x + 4, y + 1);
  ctx.stroke();
  
  // ID label
  ctx.fillStyle = '#374151';
  ctx.strokeStyle = 'white';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(x + 8, y - 5, 6, 0, 2 * Math.PI);
  ctx.fill();
  ctx.stroke();
  
  ctx.fillStyle = 'white';
  ctx.font = 'bold 8px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(id.toString(), x + 8, y - 5);
}

/**
 * Helper: Draw rounded rectangle
 */
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}
